/**
 * Cockpit entry point.
 *
 * Order of mounting (per KEN_LAYER_7 §7):
 *   1. QueryClient (TanStack Query)
 *   2. AuthProvider (validates session cookie, populates @ken/auth store)
 *   3. TenantProvider (URL-derived tenant context — never global-store-only)
 *   4. TerminologyProvider (resolved terminology from /api/shell/manifest)
 *   5. Router (TanStack Router with module routes composed at build time)
 */

import "@ken/ui/styles";

import { retryDelayMs, shouldRetryRequest } from "@ken/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { composeModuleRoutes } from "./lib/composeModuleRoutes";
import { install401Handler } from "./lib/install401Handler";
import { installErrorCapture } from "./lib/installErrorCapture";
import { AppProviders } from "./providers/AppProviders";
import { routeTree } from "./routeTree.gen";
import { Route as TenantSlugRoute } from "./routes/$tenantSlug";

// Patch window.fetch BEFORE any provider mounts. Otherwise the AuthProvider's
// initial /auth/session call would go through the un-patched fetch.
install401Handler();

// Install the error-capture listeners (window error / unhandledrejection +
// console ring buffer) next to the 401 handler. The install itself is
// boot-safe (its whole body is wrapped) and its REPORTING is gated on an
// authenticated route, so pre-auth (login) errors are dropped (ADR-0121).
installErrorCapture();

// UI-13. The old defaults were `retry: 3` with a backoff reaching 30 s, which
// meant a failed panel spent roughly fourteen seconds showing skeletons before
// its component was ever told the request had failed — read, reasonably, as
// "the screen renders blank". The policy now lives in one shared, tested place
// (@ken/ui `retry-policy`): a 4xx is never retried, because it is a decision
// rather than a hiccup, and everything else gets at most two quick retries so
// a visible message appears inside two seconds.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryRequest,
      retryDelay: retryDelayMs,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

// Mount module-contributed routes under the tenant-scoped parent before the
// router snapshots its tree. composeModuleRoutes wraps every module route in
// the per-module ErrorBoundary so a runtime throw in one module never crashes
// the shell (KEN_LAYER_7 Principle 9 + ADR-040).
//
// Important: `.addChildren([...])` REPLACES the children array on a TanStack
// Router route object. The file-based routeTree.gen.ts has already attached
// dashboard / concierge / settings under TenantSlugRoute by the time this
// module loads, so we have to preserve those and append the module routes.
const existingTenantChildren =
  ((TenantSlugRoute as unknown as { children?: unknown }).children as never[] | undefined) ??
  [];
const moduleRoutes = composeModuleRoutes(TenantSlugRoute);
TenantSlugRoute.addChildren([...existingTenantChildren, ...moduleRoutes] as never);

const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing #root element in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <RouterProvider router={router} />
      </AppProviders>
    </QueryClientProvider>
  </StrictMode>,
);
